import templates from '../../../catalogs/janus/corpse-templates.json';
import { ecId, ecLean, record, upper, type Row } from '../ability/context';
import { readItemField } from '../compile/item-field-reader';
import { readInventoryField, readInventoryRows, readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { fromBaseClone, fromBaseUuid } from '../compile/from-base-context';
import { phpIsNumeric } from '../inventory/lift-occupancy';
import { EQUIPMENT_SLOTS } from '../inventory/equipment-slots';
import { emptySpawnPlan, type SpawnReaders, type SpawnRowPlan } from '../spawn/row-plan';
import { statusItemId, statusRows, STATUS_IDS } from '../statuses/expiry';
import { statusScalar } from '../actions/activity';
import { combatCenter } from '../actions/ability-preconditions';
import { resolveGrappleRegressOpponentAndStatusRow } from '../port/k2-geometry/grapple_helpers';
import { at } from '../port/k2-geometry/support-b1';
import { trim } from '../php-compat/strings';

const templateByType: Readonly<Record<string, string>> = templates;
const HUMANOID = '7FB75162-DB32-5BCC-1B26-6EA56E201228';
type CorpseReaders = Pick<SpawnReaders, 'entries' | 'value' | 'inventory' | 'item' | 'uuid'> & { depthAuthored?: boolean };
export interface CorpseMintPlan { plan: SpawnRowPlan; inventoryId: string; grapple?: { ownerId: string; status: Row } }

/** CorpseTemplate::itemIdForCreature: first mapped type, then the humanoid default. */
export function corpseTemplateId(target: Row, read: Pick<CorpseReaders, 'entries'>): string {
    for (const entry of read.entries(target, 'creature_type')) {
        const id = templateByType[upper(readLayerListEntryUuid(entry))];
        if (id) return id;
    }
    return HUMANOID;
}

function corpseGrapple(target: Row): { ownerId: string; status: Row } | null {
    const grip = resolveGrappleRegressOpponentAndStatusRow(target, STATUS_IDS.grappled, null, { deps: {
        creature_statuses: row => statusRows(record(row), 'target'),
        item_field: (row, key) => key === 'item_id' ? statusItemId(record(row) ?? {}) : readItemField(record(row), key),
        check_uuid: fromBaseUuid,
    } });
    if (!grip) return null;
    const ownerId = upper(at(grip, 'restrainer_encounter_creature_id'));
    const status = statusRows(target, 'target').find(row => upper(statusScalar(row, 'status_id')) === upper(at(grip, 'status_row_id')))!;
    return { ownerId, status };
}

/** ft_corpse_hand_to_grappler chooses off hand, main hand, then an unequipped carried body. */
export function corpseGrip(target: Row, members: readonly Row[]): { ownerId: string; equipped: string; status: Row } | null {
    const grip = corpseGrapple(target);
    if (!grip) return null;
    const { ownerId, status } = grip;
    const grappler = members.find(row => ecId(row) === ownerId);
    if (!grappler || Number(ecLean(grappler, 'encounter_creature_dead') ?? 0) !== 0
        || Number(ecLean(grappler, 'encounter_creature_deleted') ?? 0) !== 0
        || upper(ecLean(grappler, 'encounter_id')) !== upper(ecLean(target, 'encounter_id'))) return null;
    const held = new Set(readInventoryRows(grappler, 'inventory').flatMap(entry =>
        Number(readInventoryField(entry, 'inventory_deleted') ?? 0) === 0 ? [upper(readInventoryField(entry, 'equipped'))] : []));
    const slots = EQUIPMENT_SLOTS;
    const equipped = held.has(slots.both_hands) ? '' : !held.has(slots.off_hand) ? slots.off_hand : !held.has(slots.main_hand) ? slots.main_hand : '';
    return { ownerId, equipped, status };
}

/** The body is a distinct inventory row. Existing gear keeps its own possessor; no loot is re-rolled.
 * The caller supplies the post-landing target and performs the returned grapple's linked teardown.
 * Swarm shedding is a separate body contract and must be selected before calling this ordinary mint. */
export function planCorpseMint(target: Row, killer: Row | null, members: readonly Row[], inventories: readonly Row[], read: CorpseReaders): CorpseMintPlan {
    const targetId = ecId(target), encounterId = upper(ecLean(target, 'encounter_id'));
    if (!fromBaseUuid(targetId) || !fromBaseUuid(encounterId)) throw new Error('death:corpse-identity-not-held');
    const existing = inventories.find(row => upper(readInventoryField(row, 'inventory_encounter_creature_id')) === targetId
        && Number(readInventoryField(row, 'inventory_deleted') ?? 0) === 0);
    if (existing) {
        const inventoryId = readInventoryField(existing, 'inventory_id');
        if (!fromBaseUuid(inventoryId)) throw new Error('death:existing-corpse-identity-not-held');
        return { plan: emptySpawnPlan(), inventoryId };
    }
    const at = combatCenter(target);
    if (!at) throw new Error('death:corpse-position-not-held');
    const template = read.item(corpseTemplateId(target, read));
    // add_inventory's ordinary factory owns vitality, pictures, packed contents and spoilage.
    const plan = fromBaseClone(read.inventory(template, { encounter_creatures: { encounter_creature_id: encounterId } }, 1, '', true));
    const bodies = plan.writes.filter(write => write.table === 'inventory' && write.op === 'insert'
        && upper(readItemField(write.row, 'possess_id')) === encounterId);
    if (bodies.length !== 1) throw new Error('death:corpse-factory-row-contract');
    const body = bodies[0]!, inventoryId = String(readItemField(body.row, 'inventory_id'));
    if (!fromBaseUuid(inventoryId)) throw new Error('death:corpse-factory-identity');
    const name = trim(String(ecLean(target, 'encounter_creature_name') ?? readItemField(target, 'item_name') ?? ''));
    const size = readLayerListEntryUuid(read.value(target, 'size')), weight = read.value(target, 'weight');
    const grip = corpseGrip(target, members);
    body.table = 'corpse_inventory';
    Object.assign(body.row, { inventory_name: name ? `Corpse of ${name}` : 'Corpse',
        inventory_encounter_creature_id: targetId, inventory_size: fromBaseUuid(size) ? size : null,
        weight_override: phpIsNumeric(weight) && Number(weight) >= 0 ? Number(weight) : null,
        possess_id: grip?.ownerId ?? encounterId, equipped: grip ? grip.equipped : null,
        x_pos: grip ? 0 : at.x, y_pos: grip ? 0 : at.y, z_pos: grip || !read.depthAuthored ? 0 : at.z });
    const killerId = killer ? ecId(killer) : '';
    plan.writes.push({ table: 'corpse_links', op: 'insert', row: {
        inventory_encounter_creature_link_id: read.uuid(), inventory_encounter_creature_link_inventory_id: inventoryId,
        inventory_encounter_creature_link_encounter_creature_id: targetId,
        inventory_encounter_creature_link_kind: 'corpse',
        inventory_encounter_creature_link_killer_id: fromBaseUuid(killerId) && killerId !== targetId ? killerId : null,
        inventory_encounter_creature_link_source_item_id: null, inventory_encounter_creature_link_died_round: null,
        inventory_encounter_creature_link_notes: null,
    } });
    // Even an ineligible/missing grappler loses its grapple when the victim dies.
    const grapple = corpseGrapple(target);
    if (grapple) return { plan, inventoryId, grapple };
    return { plan, inventoryId };
}
